import AddIcon from "@mui/icons-material/Add";
import { RefObject, SyntheticEvent, useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
	AddModificationButton,
	AddModificationButtonCaption,
	AddModificationButtonContainer,
	AddModificationRemoveButton,
	AddModificationTypeWrapper,
	ModificationGroup,
	ModificationSkeletonContainer
} from "./AddModificationStyles.ts";
import AutoComplete from "../../Common/Autocomplete/Autocomplete.tsx";
import {
	AddModificationButtonText,
	AddModificationIncrementError,
	AddModificationPlaceholder,
	AddModificationQuickAddText,
	AddModificationRemoveText
} from "./constants.ts";
import { models } from "types/api/viewModels.ts";
import { v4 as uuidv4 } from "uuid";
import Increment from "components/NewOrders/AddModification/components/Increment.tsx";
import { GetModificationsResponseData } from "types/api/products/getModifications.ts";
import {
	ModificationFormErrors,
	ModificationFormValues
} from "components/NewOrders/AddModification/hooks/useAddModification.ts";
import { Skeleton } from "@mui/material";
import { matchSorter } from "match-sorter";
import { getModificationOptions } from "./utils/AddModification.utils.ts";

interface AddModificationProps {
	selectedEditItem?: RefObject<models["PendingLineItemViewModel"] | models["CqModLineItemViewModel"] | undefined>;
	errors: ModificationFormErrors;
	formValues: ModificationFormValues;
	modifications: GetModificationsResponseData | undefined;
	isCustomQuote?: boolean;
	onModificationChange(values: ModificationFormValues, valueToAppendToNotes?: string | null): void;
	handleClearFieldError(errors: ModificationFormErrors): void;
}

const modListIdSymbol = Symbol("modId");

export type Modification = models["ModificationViewModel"] | models["SpecialModificationViewModel"];

type PendingModificationValue = models["ModificationViewModel"] & {
	values?: models["PendingValueViewModel"][] | null;
};
export type ModificationValues = PendingModificationValue | models["SpecialModificationViewModel"] | null | undefined;

const getOptionLabel = (item: Modification) => {
	if (item.sku === "CUSTOM") {
		return item.description ?? "";
	}
	return [item.sku, item.description].filter(Boolean).join(" - ");
};

type ModificationFilterOptions = (items: Modification[], state: { inputValue: string }) => any[];
const filterOptions: ModificationFilterOptions = (items, { inputValue }) =>
	matchSorter(items, inputValue, { keys: [(item) => getOptionLabel(item)] });

const AddModification = ({
	onModificationChange,
	formValues,
	handleClearFieldError,
	selectedEditItem,
	errors,
	modifications,
	isCustomQuote
}: AddModificationProps) => {
	// The running list of mods, only for adding/removing purposes
	const [modificationList, setModificationList] = useState<Modification[]>([]);
	const selectedPendingEditItem = selectedEditItem?.current as models["PendingLineItemViewModel"] | undefined;

	const [rangeCount, setRangeCount] = useState<number>(0);
	const isLoaded = useRef(false);

	const [focusDimensionForIndex, setFocusDimensionForIndex] = useState<number | null>(null);
	const dimensionInputRefs = useRef<Record<number, HTMLInputElement | null>>({});
	const setDimensionInputRef = useCallback(
		(index: number) => (el: HTMLInputElement | null) => {
			dimensionInputRefs.current[index] = el;
		},
		[]
	);

	useEffect(() => {
		if (focusDimensionForIndex === null) return;
		const timeoutId = window.setTimeout(() => {
			dimensionInputRefs.current[focusDimensionForIndex]?.focus();
			setFocusDimensionForIndex(null);
		}, 0);
		return () => window.clearTimeout(timeoutId);
	}, [focusDimensionForIndex]);

	useEffect(() => {
		if (
			!isLoaded.current &&
			modifications &&
			selectedPendingEditItem?.modifications &&
			selectedPendingEditItem.modifications.length > 0
		) {
			const modList = selectedPendingEditItem.modifications
				?.map((mod) => modifications.find((modByProductLine) => modByProductLine.id === mod.id))
				.filter((mod) => typeof mod !== "undefined")
				.map((mod) => ({ ...mod, [modListIdSymbol]: uuidv4() }));
			if (modList && modList.length > 0) {
				// Create initial mod list from an edited item's modifications
				setModificationList(modList);

				const map = new Map(formValues);

				// Set initial Autocomplete values from an edited item's modifications
				modList.forEach((mod) => {
					const modValues = [
						...(selectedPendingEditItem?.modifications?.find(
							(selectedItemMod) => selectedItemMod.id === mod.id
						)?.values ?? [])
					];

					map.set(mod[modListIdSymbol], { ...mod, values: modValues });
					onModificationChange(map);
				});
				isLoaded.current = true;
			}

			isLoaded.current = true;
		}
	}, [formValues, modifications, onModificationChange, selectedPendingEditItem]);

	const handleModificationChange = (
		_event: SyntheticEvent,
		value: models["ModificationViewModel"] | null | undefined,
		modification: Modification,
		index: number
	) => {
		const errorsMap = new Map(errors);
		modification[modListIdSymbol] && errorsMap.set(modification[modListIdSymbol], { selectedMod: false });
		handleClearFieldError(errorsMap);
		modification[modListIdSymbol] &&
			onModificationChange(
				new Map(formValues).set(modification[modListIdSymbol], {
					...value,
					values: value?.ranges?.map((range) => ({ description: range.description }))
				})
			);

		setModificationList(
			(prev) =>
				prev
					.map((mod, modIndex) => {
						if (modIndex === index) {
							return { ...value, [modListIdSymbol]: mod[modListIdSymbol] };
						}
						return mod;
					})
					.filter((mod) => typeof mod !== "undefined" && typeof mod !== null) ?? []
		);
		if (value?.ranges?.length) {
			setFocusDimensionForIndex(index);
		}
	};

	const handleRemoveModification = (index: number) => {
		const modToRemove = modificationList[index];

		setModificationList((prev) => prev.filter((_, i) => i !== index));

		const valuesMap = new Map(formValues);
		if (modToRemove?.[modListIdSymbol]) {
			valuesMap.delete(modToRemove[modListIdSymbol]);
		}

		const errorsMap = new Map(errors);
		if (modToRemove?.[modListIdSymbol]) {
			errorsMap.delete(modToRemove[modListIdSymbol]);
		}

		handleClearFieldError(errorsMap);
		onModificationChange(valuesMap);
	};

	const handleIncrementChange = (modification: Modification, value: number, rangeIndex: number) => {
		const map = new Map(formValues);
		const modId = modification[modListIdSymbol] ?? "";
		const newValues = map.get(modId);

		if (newValues && "values" in newValues && Array.isArray(newValues.values) && newValues.values[rangeIndex]) {
			const updatedValues = newValues.values.map((rangeValue, index) => {
				if (index === rangeIndex) {
					return { ...rangeValue, value };
				}
				return rangeValue;
			});
			map.set(modId, { ...modification, values: updatedValues });
		}
		onModificationChange(map);

		const errorsMap = new Map(errors);
		const newErrors = modification[modListIdSymbol] ? errorsMap.get(modification[modListIdSymbol]) : undefined;
		if (
			newErrors &&
			"values" in newErrors &&
			Array.isArray(newErrors.values) &&
			typeof newErrors.values[rangeIndex] !== "undefined"
		) {
			newErrors.values[rangeIndex] = false;
		}
		if (newErrors && modification[modListIdSymbol]) {
			errorsMap.set(modification[modListIdSymbol], newErrors);
		}
	};

	const modificationTypeErrorState = useMemo(
		() => modificationList.map((modification) => modification.description === ""),
		[modificationList]
	);

	useEffect(() => {
		const getRangeCount = document.querySelectorAll("#add-line-item-modification-range-select");
		setRangeCount(getRangeCount.length);
	}, [modificationTypeErrorState]);

	const addModification = useCallback(() => {
		const newId = uuidv4();
		const newMod = { [modListIdSymbol]: newId } as Modification;

		setModificationList((prev) => [...prev, newMod]);
		onModificationChange(new Map(formValues).set(newId, {}));
	}, [formValues, onModificationChange]);

	useEffect(() => {
		if (!isCustomQuote) {
			return;
		}

		const handleQuickKeys = (event: KeyboardEvent) => {
			if (event.key === "Tab" && event.shiftKey) {
				event.preventDefault();
				addModification();
			}
		};

		document.addEventListener("keydown", handleQuickKeys);

		return () => {
			document.removeEventListener("keydown", handleQuickKeys);
		};
	}, [addModification, isCustomQuote]);

	const getModificationValue = (
		formValues: ModificationFormValues,
		modId: string | undefined,
		rangeIndex: number
	) => {
		const modValue = formValues.get(modId ?? "");
		if (modValue && "values" in modValue && Array.isArray(modValue.values) && modValue.values[rangeIndex]) {
			return modValue.values[rangeIndex];
		}
		return undefined;
	};

	return (
		<div>
			<ModificationGroup>
				{!isLoaded.current && !modifications && (
					<div>
						<ModificationSkeletonContainer>
							<Skeleton height={24} />
							<Skeleton height={24} />
						</ModificationSkeletonContainer>

						<ModificationSkeletonContainer>
							<Skeleton height={24} />
							<Skeleton height={24} />
						</ModificationSkeletonContainer>

						<Skeleton
							height={24}
							width="50%"
						/>
						<Skeleton
							height={24}
							width="50%"
						/>
					</div>
				)}
				{modificationList.map((singleMod, index) => {
					return (
						<div key={singleMod[modListIdSymbol]}>
							<AddModificationTypeWrapper
								key={selectedEditItem?.current?.id}
								multipleRanges={rangeCount > 1}
							>
								<AutoComplete
									options={getModificationOptions(
										singleMod,
										modListIdSymbol,
										formValues,
										modifications
									)}
									onChange={(event, value) =>
										handleModificationChange(event, value, singleMod, index)
									}
									required
									getOptionLabel={getOptionLabel}
									isError={errors.get(singleMod[modListIdSymbol] ?? "")?.selectedMod}
									errorText={AddModificationIncrementError}
									label={AddModificationPlaceholder}
									dataTestId="add-line-item-modification-select"
									disableClearable
									virtualize
									value={formValues.get(singleMod[modListIdSymbol] ?? "")}
									autoFocus
									filterOptions={filterOptions}
								/>

								{"ranges" in singleMod &&
									singleMod?.ranges?.map((rangeData, rangeIndex: number) => (
										<Increment
											key={rangeIndex}
											handleIncrementChange={handleIncrementChange}
											value={getModificationValue(
												formValues,
												singleMod[modListIdSymbol],
												rangeIndex
											)}
											rangeData={rangeData}
											modification={singleMod}
											rangeIndex={rangeIndex}
											isError={errors.get(singleMod[modListIdSymbol] ?? "")?.values?.[rangeIndex]}
											id="add-line-item-modification-range-select"
											inputRef={rangeIndex === 0 ? setDimensionInputRef(index) : undefined}
										/>
									))}

								<AddModificationRemoveButton
									variant="remove"
									data-testid="add-line-item-modification-remove-button"
									onClick={() => handleRemoveModification(index)}
								>
									{AddModificationRemoveText}
								</AddModificationRemoveButton>
							</AddModificationTypeWrapper>
						</div>
					);
				})}
				<AddModificationButtonContainer>
					<AddModificationButton
						variant="text"
						data-testid="add-line-item-modification-button"
						onClick={addModification}
					>
						<AddIcon />
						{AddModificationButtonText}
					</AddModificationButton>
					{isCustomQuote && (
						<AddModificationButtonCaption>{AddModificationQuickAddText}</AddModificationButtonCaption>
					)}
				</AddModificationButtonContainer>
			</ModificationGroup>
		</div>
	);
};

export default AddModification;
